'use client';

import { useUnit } from 'effector-react';
import { useEffect, useRef } from 'react';
import './AccessibilityRuntime.module.scss';

import {
    ACCESSIBILITY_STORAGE_KEY,
    DEFAULT_ACCESSIBILITY_SELECTION,
    DEFAULT_FOCUS_HIGHLIGHT_SELECTION,
    FOCUS_HIGHLIGHT_STORAGE_KEY,
    normalizeAccessibilitySelection,
    normalizeFocusHighlightSelection,
    resolveEffectiveFocusHighlightSelection,
    type AccessibilitySelection,
    type FocusHighlightSelection,
} from '@/features/accessibility/logic';
import {
    $accessibilitySelection,
    $focusHighlightSelection,
    accessibilityChanged,
    accessibilityHydrated,
    focusHighlightChanged,
    focusHighlightHydrated,
} from '@/features/accessibility/model';
import { scrubAccessibilityMutationRecords, stripAccessibilityAttributes } from '@/features/accessibility/runtime';
import { getDocumentOrNull, getWindowOrNull } from '@/lib/browser/runtime';

const getStoredAccessibilitySelection = (): AccessibilitySelection => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return DEFAULT_ACCESSIBILITY_SELECTION;
    }

    return normalizeAccessibilitySelection(browserWindow.localStorage.getItem(ACCESSIBILITY_STORAGE_KEY));
};

const getStoredSelection = (): FocusHighlightSelection => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return DEFAULT_FOCUS_HIGHLIGHT_SELECTION;
    }

    return normalizeFocusHighlightSelection(browserWindow.localStorage.getItem(FOCUS_HIGHLIGHT_STORAGE_KEY));
};

export const AccessibilityRuntime = ({ children }: { children: React.ReactNode }) => {
    const [accessibilitySelection, focusHighlightSelection] = useUnit([$accessibilitySelection, $focusHighlightSelection]);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        accessibilityHydrated(getStoredAccessibilitySelection());
        focusHighlightHydrated(getStoredSelection());
    }, []);

    useEffect(() => {
        const browserDocument = getDocumentOrNull();
        const browserWindow = getWindowOrNull();
        if (!browserDocument || !browserWindow) {
            return;
        }

        const effectiveFocusSelection = resolveEffectiveFocusHighlightSelection(accessibilitySelection, focusHighlightSelection);
        browserDocument.documentElement.dataset.accessibility = accessibilitySelection;
        browserDocument.documentElement.dataset.focusHighlights = effectiveFocusSelection;

        try {
            browserWindow.localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, accessibilitySelection);
            browserWindow.localStorage.setItem(FOCUS_HIGHLIGHT_STORAGE_KEY, focusHighlightSelection);
        } catch {
            // Best effort only.
        }

        const bootstrapObserverWindow = browserWindow as Window & {
            __chattengerAccessibilityObserver?: MutationObserver | null;
        };
        bootstrapObserverWindow.__chattengerAccessibilityObserver?.disconnect();
        bootstrapObserverWindow.__chattengerAccessibilityObserver = null;

        observerRef.current?.disconnect();
        observerRef.current = null;

        if (accessibilitySelection === 'off') {
            stripAccessibilityAttributes(browserDocument.body || browserDocument.documentElement);
            const observer = new MutationObserver((records) => {
                scrubAccessibilityMutationRecords(records);
            });
            observer.observe(browserDocument.documentElement, {
                subtree: true,
                childList: true,
                attributes: true,
            });
            observerRef.current = observer;
        }

        return () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, [accessibilitySelection, focusHighlightSelection]);

    return <>{children}</>;
};

export const updateFocusHighlightSelection = (selection: FocusHighlightSelection) => {
    focusHighlightChanged(selection);
};

export const updateAccessibilitySelection = (
    selection: AccessibilitySelection,
    options?: {
        reload?: boolean;
    }
) => {
    const browserDocument = getDocumentOrNull();
    const browserWindow = getWindowOrNull();

    accessibilityChanged(selection);

    if (browserDocument) {
        browserDocument.documentElement.dataset.accessibility = selection;
        browserDocument.documentElement.dataset.focusHighlights =
            selection === 'off' ? 'off' : normalizeFocusHighlightSelection(browserWindow?.localStorage.getItem(FOCUS_HIGHLIGHT_STORAGE_KEY));
    }

    if (browserWindow) {
        try {
            browserWindow.localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, selection);
        } catch {
            // Best effort only.
        }

        const shouldReload = options?.reload ?? process.env.NODE_ENV !== 'test';
        if (shouldReload) {
            browserWindow.location.reload();
        }
    }
};
